import { useMemo } from 'react';
import { Box, Grid } from '@mui/material';

import {
  useParentSupervisionEditable,
  useProcedureTimeRestrictions,
} from '../../../../../../hooks';
import {
  PARENT_SN_VERSIONS,
  SUPERVISION_LOCATION_V2,
  SUPERVISION_LOCATIONS,
  SUPERVISION_SETTING_V2,
  SUPERVISION_SETTINGS,
} from '../../../constants';
import {
  useClientOptions,
  useParentOptions,
  useParentSupervisionForm,
  useSupervisionEventValidation,
  useDeleteSupervision,
  useDownloadPDF,
  useEditSupervision,
  useBackdateSupervision,
  useSupervisionActions,
} from '../../hooks';
import { getSelectedClientZipCode } from '../../utils/general';
import { getButtonVisibilityValues } from '../../utils/buttonVisibility';
import BackdateForm from './backdateForm';
import ParentInformation from './parentInformation';
import SessionControls from './sessionControls';
import SupervisionActions from './supervisionActions';
import DurationEventWarning from './durationEventWarning';
import RateEventWarning from './rateEventWarning';
import FHConfirmDialog from '../../../../../base/dialogs/FHConfirmDialog';
import { FORTA_BLUE } from '../../../../../../utils/colors';

import type { IParentSupervision } from '../../../../../../utils/types';
import type { ParentSupervisionUpdateHandler } from '../../../types';

interface ControlsProps {
  parentSNLoading: boolean;
  isSupervisionCompleted: boolean;
  parentSupervision: IParentSupervision | null;
  isParentsClearable: boolean;
  onUpdate: ParentSupervisionUpdateHandler;
}

function Controls({
  isSupervisionCompleted,
  parentSNLoading,
  parentSupervision,
  isParentsClearable,
  onUpdate,
}: ControlsProps) {
  const isEditable = useParentSupervisionEditable() && !parentSNLoading;
  const showV1 = parentSupervision?.version === PARENT_SN_VERSIONS.V1;
  const locations = showV1 ? SUPERVISION_LOCATIONS : SUPERVISION_LOCATION_V2;
  const settings = showV1 ? SUPERVISION_SETTINGS : SUPERVISION_SETTING_V2;

  const { values, hasPersistedSupervision, handleUpdateField, handleUpdateFieldObject } =
    useParentSupervisionForm({
      parentSupervision,
      settings,
      onUpdate,
    });

  const { clients, clientOptions, selectedClientDetails, user } = useClientOptions({
    selectedClientId: values?.client?.id,
  });

  const { parentOneOptions, parentTwoOptions } = useParentOptions({
    selectedClientId: values?.client?.id,
    parentOneId:
      values?.parent_one_parent_and_caregiver_id ??
      values?.parent_one_parent_and_caregiver?.id ??
      undefined,
    parentTwoId:
      values?.parent_two_parent_and_caregiver_id ??
      values?.parent_two_parent_and_caregiver?.id ??
      undefined,
  });

  const selectedClientZipCode = useMemo(
    () => getSelectedClientZipCode(clients, values?.client?.id),
    [clients, values?.client?.id],
  );

  const {
    timeToolTipOpen,
    durationToolTipOpen,
    startBetween10to12,
    endBetween10to12,
    zone,
    isTZIdentified,
    abbreviatedZone,
  } = useProcedureTimeRestrictions(
    values?.date_start,
    values?.expected_duration ?? 0,
    Boolean(!values?.date_start || (hasPersistedSupervision && !isEditable)),
    selectedClientZipCode,
  );

  const supervisionId = parentSupervision?.id;
  const { showConfirmDeleteDialog, setShowConfirmDeleteDialog, handleDeleteSupervision } =
    useDeleteSupervision(supervisionId);

  const { showConfirmDownloadDialog, setShowConfirmDownloadDialog, handleDownloadPDF } =
    useDownloadPDF(supervisionId);

  const { showConfirmEditDialog, setShowConfirmEditDialog, handleEditSupervision } =
    useEditSupervision(supervisionId);

  const {
    showConfirmBackdateDialog,
    setShowConfirmBackdateDialog,
    requestBackdate,
    handleBackdateSupervision,
  } = useBackdateSupervision(supervisionId);

  const { submitting, handleStartNotes } = useSupervisionActions({ values, parentSupervision });

  const {
    showRateEventWarning,
    setShowRateEventWarning,
    showDurationEventWarning,
    setShowDurationEventWarning,
    handleTimeRangeUpdate,
  } = useSupervisionEventValidation({
    parentSupervision,
    values,
    onValidTimeRangeUpdate: handleUpdateField,
  });

  const handleClientUpdate = (clientId?: number) => {
    if (!clientId) return;

    handleUpdateFieldObject({
      client: {
        ...values?.client,
        id: clientId,
      },
    });
  };

  const handleParentOneUpdate = (parentId: number) => {
    handleUpdateFieldObject({
      parent_one_parent_and_caregiver_id: parentId,
      parent_one_parent_and_caregiver: undefined,
    });
  };

  const handleParentTwoUpdate = (parentId: number) => {
    handleUpdateFieldObject({
      parent_two_parent_and_caregiver_id: parentId,
      parent_two_parent_and_caregiver: undefined,
    });
  };

  const { showDeleteButton, showEditButton, showBackdateForm, isStartDisabled } =
    getButtonVisibilityValues(
      parentSupervision,
      user?.roles,
      submitting,
      hasPersistedSupervision,
      values,
      timeToolTipOpen,
      durationToolTipOpen,
      user?.email,
    );

  return (
    <>
      {showBackdateForm && (
        <Box pt={4}>
          <BackdateForm parentSupervision={parentSupervision} onBackdate={requestBackdate} />
        </Box>
      )}
      <Grid container pt={4} spacing={2} alignItems="center">
        <SessionControls
          isSupervisionCompleted={isSupervisionCompleted}
          parentSupervision={parentSupervision}
          values={values}
          isEditable={isEditable}
          hasPersistedSupervision={hasPersistedSupervision}
          showV1={showV1}
          locations={locations}
          settings={settings}
          clientOptions={clientOptions}
          selectedClientDetails={selectedClientDetails}
          timeToolTipOpen={timeToolTipOpen}
          durationToolTipOpen={durationToolTipOpen}
          startBetween10to12={startBetween10to12}
          endBetween10to12={endBetween10to12}
          zone={zone}
          isTZIdentified={isTZIdentified}
          abbreviatedZone={abbreviatedZone}
          onClientUpdate={handleClientUpdate}
          onTimeRangeUpdate={handleTimeRangeUpdate}
          onFieldUpdate={handleUpdateField}
        />
        <SupervisionActions
          isSupervisionCompleted={isSupervisionCompleted}
          showDeleteButton={showDeleteButton}
          showEditButton={showEditButton}
          isStartDisabled={isStartDisabled}
          onStart={handleStartNotes}
          onOpenDeleteDialog={() => setShowConfirmDeleteDialog(true)}
          onOpenDownloadDialog={() => setShowConfirmDownloadDialog(true)}
          onOpenEditDialog={() => setShowConfirmEditDialog(true)}
        />
      </Grid>

      <ParentInformation
        isSupervisionCompleted={isSupervisionCompleted}
        isEditable={isEditable}
        isParentsClearable={isParentsClearable}
        parentSupervision={parentSupervision}
        values={values}
        parentOneOptions={parentOneOptions}
        parentTwoOptions={parentTwoOptions}
        onParentOneUpdate={handleParentOneUpdate}
        onParentTwoUpdate={handleParentTwoUpdate}
        onClearParentOne={() => {
          handleUpdateFieldObject({
            parent_one_parent_and_caregiver: undefined,
            parent_one_parent_and_caregiver_id: null,
            parent_two_parent_and_caregiver: undefined,
            parent_two_parent_and_caregiver_id: null,
          });
        }}
        onClearParentTwo={() => {
          handleUpdateFieldObject({
            parent_two_parent_and_caregiver: undefined,
            parent_two_parent_and_caregiver_id: null,
          });
        }}
      />

      <FHConfirmDialog
        title="Delete parent supervision"
        content="Are you sure you want to delete this parent supervision? This action cannot be undone."
        submittext="Yes, delete"
        canceltext="Cancel"
        submitBtnTestId="deleteBtn"
        cancelBtnTestId="cancelBtn"
        open={showConfirmDeleteDialog}
        onClose={() => setShowConfirmDeleteDialog(false)}
        onSubmit={handleDeleteSupervision}
      />
      <FHConfirmDialog
        title="Download parent supervision"
        content="Are you sure you want to download this parent supervision?"
        submittext="Download"
        canceltext="Cancel"
        open={showConfirmDownloadDialog}
        onClose={() => setShowConfirmDownloadDialog(false)}
        onSubmit={handleDownloadPDF}
      />
      <FHConfirmDialog
        title="Make editable"
        content="Are you sure you want to make this parent supervision note editable?"
        submittext="Yes"
        canceltext="Cancel"
        open={showConfirmEditDialog}
        onClose={() => setShowConfirmEditDialog(false)}
        onSubmit={handleEditSupervision}
      />
      <FHConfirmDialog
        open={showRateEventWarning}
        title="Events outside the session time"
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        dialogStyles={{
          maxHeight: '85vh',
          height: 'auto',
          width: '650px',
          maxWidth: '650px',
        }}
        contentRenderer={() => <RateEventWarning />}
        submittext="Okay"
        hideCancelButton
        onClose={() => {
          setShowRateEventWarning(false);
        }}
        onSubmit={() => {
          setShowRateEventWarning(false);
        }}
      />
      <FHConfirmDialog
        open={showDurationEventWarning}
        title="Duration events outside the session time"
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        dialogStyles={{
          maxHeight: '85vh',
          height: 'auto',
          width: '650px',
          maxWidth: '650px',
        }}
        contentRenderer={() => <DurationEventWarning />}
        submittext="Okay"
        hideCancelButton
        onClose={() => {
          setShowDurationEventWarning(false);
        }}
        onSubmit={() => {
          setShowDurationEventWarning(false);
        }}
      />
      <FHConfirmDialog
        open={showConfirmBackdateDialog}
        title="Change"
        content="Are you sure you want to change the start date of this parent supervision note?"
        submittext="Yes"
        canceltext="Cancel"
        onClose={() => setShowConfirmBackdateDialog(false)}
        onSubmit={handleBackdateSupervision}
      />
    </>
  );
}

export default Controls;
